import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { authService } from '../services/auth';
import { foldersService } from '../services/folders';
import { audioFilesService } from '../services/audioFiles';
import { transcriptsService } from '../services/transcripts';
import { useUndoRedo } from '../hooks/useUndoRedo';
import { useDashboardEvents } from '../hooks/useDashboardEvents';
import { useWebSocket } from '../hooks/useWebSocket';
import { Folder, AudioFile, FileStatusUpdate, Transcript } from '../types';
import CollapsibleSidebar from '../components/ui/CollapsibleSidebar';
import FilesSidebar from '../components/filetree/FilesSidebar';
import TranscriptToolsSidebar from '../components/tools/TranscriptToolsSidebar';
import Navbar from '../components/dashboard/Navbar';
import FileView from '../components/dashboard/FileView';
import FolderView from '../components/dashboard/FolderView';
import WelcomeView from '../components/dashboard/WelcomeView';
import { UserPreferencesModal } from '../components/tools/UserPreferencesModal';
import HelpBubble from '../components/help/HelpBubble';

const Dashboard: React.FC = () => {
  const { state, dispatch } = useApp();
  const { preferences, loading, setSidebarState, setTheme } = usePreferences();
  const navigate = useNavigate();
  const {
    selectAudioFile,
    selectAudioFileWithoutNav,
    selectFolderWithoutNav,
    clearSelectionWithoutNav,
  } = useAppNavigation();
  const { fileId, folderId } = useParams<{
    fileId?: string;
    folderId?: string;
  }>();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [files, setFiles] = useState<AudioFile[]>([]);
  const [allFiles, setAllFiles] = useState<AudioFile[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Local sidebar states for immediate UI updates
  const [isFilesSidebarCollapsed, setIsFilesSidebarCollapsed] = useState(
    preferences.sidebar?.files_collapsed ?? false
  );
  const [isToolsSidebarCollapsed, setIsToolsSidebarCollapsed] = useState(
    preferences.sidebar?.tools_collapsed ?? false
  );

  // Local theme state for immediate UI updates
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('light');
  const [currentTranscript, setCurrentTranscript] = useState<Transcript | null>(
    null
  );
  const [utteranceRefreshTrigger, setUtteranceRefreshTrigger] = useState(0);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);

  // Track if we've done the initial sync to prevent further syncing
  const hasInitialSynced = useRef(false);

  // Sync preferences when they first become available (only once)
  useEffect(() => {
    if (!loading && preferences && !hasInitialSynced.current) {
      if (preferences.sidebar) {
        setIsFilesSidebarCollapsed(
          preferences.sidebar.files_collapsed ?? false
        );
        setIsToolsSidebarCollapsed(
          preferences.sidebar.tools_collapsed ?? false
        );
      }

      // Sync theme and apply to DOM
      const theme = preferences.theme ?? 'light';
      setCurrentTheme(theme);
      const root = document.documentElement;
      if (theme === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }

      hasInitialSynced.current = true;
    }
  }, [loading, preferences]);

  // Generic sidebar handlers - update UI immediately, then async save preferences
  const handleSidebarToggle = useCallback(
    (side: 'files' | 'tools') => {
      const isCollapsed =
        side === 'files' ? isFilesSidebarCollapsed : isToolsSidebarCollapsed;
      const newCollapsed = !isCollapsed;

      if (side === 'files') {
        setIsFilesSidebarCollapsed(newCollapsed);
      } else {
        setIsToolsSidebarCollapsed(newCollapsed);
      }

      // Fire-and-forget preference update
      setSidebarState(side, newCollapsed).catch((error) => {
        console.error(`Failed to save ${side} sidebar preference:`, error);
      });
    },
    [isFilesSidebarCollapsed, isToolsSidebarCollapsed, setSidebarState]
  );

  const handleSidebarCollapse = useCallback(
    (side: 'files' | 'tools') => {
      if (side === 'files') {
        setIsFilesSidebarCollapsed(true);
      } else {
        setIsToolsSidebarCollapsed(true);
      }

      // Fire-and-forget preference update
      setSidebarState(side, true).catch((error) => {
        console.error(`Failed to save ${side} sidebar preference:`, error);
      });
    },
    [setSidebarState]
  );

  // Theme handler - update UI immediately, then async save preferences
  const handleThemeToggle = useCallback(() => {
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';

    // Update UI immediately
    setCurrentTheme(newTheme);

    // Apply theme to DOM immediately
    const root = document.documentElement;
    if (newTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Fire-and-forget preference update
    setTheme(newTheme).catch((error) => {
      console.error('Failed to save theme preference:', error);
    });
  }, [currentTheme, setTheme]);

  // Preferences modal handlers
  const handleOpenPreferences = useCallback(() => {
    setIsPreferencesModalOpen(true);
  }, []);

  const handleClosePreferences = useCallback(() => {
    setIsPreferencesModalOpen(false);
  }, []);

  // Handle WebSocket export completion updates
  const handleExportCompleted = useCallback((_update: any) => {
    // Export completion is handled by the useWebSocket hook's default notification logic
  }, []);

  // Helper function to update folder statistics when files change
  const updateFolderStatistics = useCallback(
    (updatedAllFiles: AudioFile[]) => {
      if (state.selectedFolder) {
        const selectedFolder = state.selectedFolder;

        if (selectedFolder.id === 'common') {
          // Update Common folder stats
          const commonFiles = updatedAllFiles.filter((f) => !f.folderId);
          selectFolderWithoutNav({
            ...selectedFolder,
            fileCount: commonFiles.length,
            totalSize: commonFiles.reduce(
              (sum, f) => sum + (f.fileSize || 0),
              0
            ),
            transcribedCount: commonFiles.filter(
              (f) => f.hasTranscript && f.status === 'ready'
            ).length,
          });
        } else {
          // Update specific folder stats
          const folderFiles = updatedAllFiles.filter(
            (f) => f.folderId === selectedFolder.id
          );
          selectFolderWithoutNav({
            ...selectedFolder,
            fileCount: folderFiles.length,
            totalSize: folderFiles.reduce(
              (sum, f) => sum + (f.fileSize || 0),
              0
            ),
            transcribedCount: folderFiles.filter(
              (f) => f.hasTranscript && f.status === 'ready'
            ).length,
          });
        }
      }
    },
    [state.selectedFolder, selectFolderWithoutNav]
  );

  // Handle WebSocket file status updates
  const handleFileStatusUpdate = useCallback(
    (update: FileStatusUpdate) => {
      const { file_id, file: updatedFileData } = update;

      // Update the file in both allFiles and files arrays
      const updatedAllFiles = allFiles.map((file) =>
        file.id === file_id
          ? {
              ...file,
              status: updatedFileData.status as AudioFile['status'],
              hasTranscript: updatedFileData.has_transcript,
            }
          : file
      );

      const updatedFiles = files.map((file) =>
        file.id === file_id
          ? {
              ...file,
              status: updatedFileData.status as AudioFile['status'],
              hasTranscript: updatedFileData.has_transcript,
            }
          : file
      );

      setAllFiles(updatedAllFiles);
      setFiles(updatedFiles);

      // Update folder statistics with the new file states
      updateFolderStatistics(updatedAllFiles);

      // If the currently selected file is the one being updated, update it in the global state
      if (state.selectedAudioFile?.id === file_id) {
        const updatedSelectedFile = {
          ...state.selectedAudioFile,
          status: updatedFileData.status as AudioFile['status'],
          hasTranscript: updatedFileData.has_transcript,
        };
        selectAudioFile(updatedSelectedFile);

        // If it's now ready and has transcript, refresh transcript view
        if (
          updatedFileData.status === 'ready' &&
          updatedFileData.has_transcript
        ) {
          setUtteranceRefreshTrigger((prev) => prev + 1);
        }
      }

      // Auto-navigate to file when transcript becomes ready (if not already selected)
      if (
        updatedFileData.status === 'ready' &&
        updatedFileData.has_transcript &&
        state.selectedAudioFile?.id !== file_id
      ) {
        const fileToSelect = updatedAllFiles.find((f) => f.id === file_id);
        if (fileToSelect) {
          selectAudioFile(fileToSelect);
        }
      }
    },
    [
      allFiles,
      files,
      state.selectedAudioFile,
      updateFolderStatistics,
      selectAudioFile,
    ]
  );

  // Initialize WebSocket connection
  useWebSocket({
    onFileStatusChange: handleFileStatusUpdate,
    onExportCompleted: handleExportCompleted,
    autoConnect: true,
  });

  const loadFolders = async () => {
    try {
      const folders = await foldersService.getFolders();
      setFolders(folders || []);
    } catch (error) {
      console.error('Failed to load folders:', error);
      setFolders([]);
    }
  };

  const loadAllFiles = useCallback(async () => {
    try {
      const response = await audioFilesService.getAudioFiles();
      setAllFiles(response.data || []);
    } catch (error) {
      console.error('Failed to load all files:', error);
      setAllFiles([]);
    }
  }, []);

  const loadFiles = useCallback(async () => {
    try {
      // Explicitly pass null for Common folder (when currentFolderId is null)
      // Pass specific folderId for other folders
      const response = await audioFilesService.getAudioFiles(currentFolderId);
      setFiles(response.data || []);
    } catch (error) {
      console.error('Failed to load files:', error);
      setFiles([]);
    }
  }, [currentFolderId]);

  useEffect(() => {
    loadFolders();
    loadAllFiles();
  }, [loadAllFiles]);

  // Reload files and folders when user changes (logout, login as another user)
  useEffect(() => {
    if (state.user) {
      // Clear all state from previous user
      setCurrentTranscript(null);
      setFolders([]);
      setFiles([]);
      setAllFiles([]);
      clearSelectionWithoutNav();
      // Then reload data for new user
      loadFolders();
      loadAllFiles();
    }
  }, [state.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle URL-based file/folder selection (only for initial load and browser navigation)
  useEffect(() => {
    // Use non-navigating versions to prevent infinite loops
    if (fileId && allFiles.length > 0) {
      const file = allFiles.find((f) => f.id === fileId);
      if (file && state.selectedAudioFile?.id !== fileId) {
        selectAudioFileWithoutNav(file);
      }
    } else if (folderId) {
      if (folderId === 'common') {
        // Handle Common folder
        const commonFiles = allFiles.filter((f) => !f.folderId);
        const commonFolder = {
          id: 'common',
          name: 'Common',
          createdAt: state.user?.created_at || new Date().toISOString(),
          fileCount: commonFiles.length,
          totalSize: commonFiles.reduce((sum, f) => sum + (f.fileSize || 0), 0),
          transcribedCount: commonFiles.filter(
            (f) => f.hasTranscript && f.status === 'ready'
          ).length,
        };
        if (state.selectedFolder?.id !== 'common') {
          selectFolderWithoutNav(commonFolder);
          setCurrentFolderId(null);
        }
      } else {
        // Handle regular folder
        const folder = folders.find((f) => f.id === folderId);
        if (folder && state.selectedFolder?.id !== folderId) {
          const folderFiles = allFiles.filter((f) => f.folderId === folderId);
          const folderWithStats = {
            ...folder,
            fileCount: folderFiles.length,
            totalSize: folderFiles.reduce(
              (sum, f) => sum + (f.fileSize || 0),
              0
            ),
            transcribedCount: folderFiles.filter(
              (f) => f.hasTranscript && f.status === 'ready'
            ).length,
          };
          selectFolderWithoutNav(folderWithStats);
          setCurrentFolderId(folderId);
        }
      }
    } else if (!fileId && !folderId) {
      // Clear selections when navigating to base dashboard
      if (state.selectedAudioFile || state.selectedFolder) {
        clearSelectionWithoutNav();
        setCurrentFolderId(null);
      }
    }
  }, [
    fileId,
    folderId,
    allFiles,
    folders,
    state.selectedAudioFile,
    state.selectedFolder,
    state.user?.created_at,
    selectAudioFileWithoutNav,
    selectFolderWithoutNav,
    clearSelectionWithoutNav,
  ]);

  useEffect(() => {
    loadFiles();
  }, [currentFolderId, loadFiles]);

  useEffect(() => {
    const fetchTranscript = async () => {
      if (
        state.selectedAudioFile?.hasTranscript &&
        state.selectedAudioFile.id
      ) {
        try {
          const transcript = await transcriptsService.getTranscript(
            state.selectedAudioFile.id
          );
          setCurrentTranscript(transcript);
        } catch (error) {
          console.error('Failed to fetch transcript:', error);
          setCurrentTranscript(null);
        }
      } else {
        setCurrentTranscript(null);
      }
    };

    fetchTranscript();
  }, [state.selectedAudioFile]);

  const handleSpeakerUpdated = async () => {
    setUtteranceRefreshTrigger((prev) => prev + 1);
    // Refresh transcript to include new speaker data
    await handleSpeakerStatisticsRefresh();
  };

  const handleTranscriptUpdated = (updatedTranscript: Transcript) => {
    setCurrentTranscript(updatedTranscript);
  };

  // Lighter-weight refresh for speaker statistics only
  const handleSpeakerStatisticsRefresh = useCallback(async () => {
    if (state.selectedAudioFile?.hasTranscript && state.selectedAudioFile.id) {
      try {
        const transcript = await transcriptsService.getTranscript(
          state.selectedAudioFile.id
        );
        setCurrentTranscript(transcript);
      } catch (error) {
        console.error('Failed to refresh speaker statistics:', error);
      }
    }
  }, [state.selectedAudioFile]);

  // Stable error handler for edit history
  const handleEditHistoryError = useCallback((error: string) => {
    console.error('Edit history error:', error);
  }, []);

  // Central edit history management - only when there's a transcript
  const shouldLoadEditHistory = Boolean(
    state.selectedAudioFile?.id && state.selectedAudioFile?.hasTranscript
  );

  const editHistoryHook = useUndoRedo({
    audioFileId: shouldLoadEditHistory ? state.selectedAudioFile!.id : '',
    onOperationComplete: async () => {
      // Refresh utterances in FileView
      setUtteranceRefreshTrigger((prev) => prev + 1);

      // Refresh transcript data (including speakers) for the sidebar
      if (
        state.selectedAudioFile?.hasTranscript &&
        state.selectedAudioFile.id
      ) {
        try {
          const transcript = await transcriptsService.getTranscript(
            state.selectedAudioFile.id
          );
          setCurrentTranscript(transcript);
        } catch (error) {
          console.error(
            'Failed to refresh transcript after undo/redo operation:',
            error
          );
        }
      }
    },
    onError: handleEditHistoryError,
  });

  // Use generic event handlers
  const eventHandlers = useDashboardEvents(
    {
      folders,
      files,
      allFiles,
      currentFolderId,
      setFolders,
      setFiles,
      setAllFiles,
      setCurrentFolderId,
      loadFiles,
      loadAllFiles,
      updateFolderStatistics,
      setCurrentTranscript,
    },
    {
      setIsUploading,
    }
  );

  // Service wrapper functions (these handle API calls and delegate to event handlers)
  const handleCreateFolder = async (folderData: { name: string }) => {
    try {
      const newFolder = await foldersService.createFolder(folderData);
      eventHandlers.onFolderCreated(newFolder);
    } catch (error) {
      console.error('Failed to create folder:', error);
      throw error;
    }
  };

  const handleEditFolder = async (
    folderId: string,
    folderData: { name: string }
  ) => {
    try {
      const updatedFolder = await foldersService.updateFolder(
        folderId,
        folderData
      );
      eventHandlers.onFolderUpdated(folderId, updatedFolder);
    } catch (error) {
      console.error('Failed to update folder:', error);
      throw error;
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    try {
      await foldersService.deleteFolder(folderId);
      eventHandlers.onFolderDeleted(folderId);
    } catch (error) {
      console.error('Failed to delete folder:', error);
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      dispatch({ type: 'CLEAR_USER' });
      navigate('/signin');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (!state.user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Top Navigation */}
      <Navbar
        currentTheme={currentTheme}
        onThemeToggle={handleThemeToggle}
        onOpenPreferences={handleOpenPreferences}
        onLogout={handleLogout}
        onToggleFilesSidebar={() => handleSidebarToggle('files')}
        onToggleToolsSidebar={() => handleSidebarToggle('tools')}
      />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar */}
        <CollapsibleSidebar
          isCollapsed={isFilesSidebarCollapsed}
          onToggle={() => handleSidebarToggle('files')}
          side="left"
        >
          <FilesSidebar
            folders={folders}
            files={allFiles}
            currentFolderId={currentFolderId}
            onCreateFolder={handleCreateFolder}
            onEditFolder={handleEditFolder}
            onDeleteFolder={handleDeleteFolder}
            onFolderSelect={eventHandlers.onFolderSelected}
            onFilesUpload={eventHandlers.onUploadStarted}
            onFileUploaded={eventHandlers.onUploadCompleted}
            onUploadError={eventHandlers.onUploadError}
            isUploading={isUploading}
            onCollapse={() => handleSidebarCollapse('files')}
            setFiles={setAllFiles}
            loadFiles={loadAllFiles}
            onFileDeleted={eventHandlers.onFileDeleted}
            onFileMoved={eventHandlers.onFileMoved}
            onFileEdited={eventHandlers.onFileUpdated}
          />
        </CollapsibleSidebar>

        {/* Main Content Area */}
        <div className="flex-1 flex min-w-0">
          {/* Center Content */}
          <div className="flex-1 p-3 md:p-4 min-w-0">
            {state.selectedAudioFile &&
            allFiles.some((f) => f.id === state.selectedAudioFile?.id) ? (
              <FileView
                audioFile={state.selectedAudioFile}
                transcript={currentTranscript || undefined}
                editHistoryHook={editHistoryHook}
                refreshTrigger={utteranceRefreshTrigger}
                onSpeakerStatisticsRefresh={handleSpeakerStatisticsRefresh}
              />
            ) : state.selectedFolder ? (
              <FolderView folder={state.selectedFolder} files={files} />
            ) : (
              <WelcomeView allFiles={allFiles} />
            )}
          </div>

          {/* Right Sidebar - Transcript Tools */}
          <CollapsibleSidebar
            isCollapsed={isToolsSidebarCollapsed}
            onToggle={() => handleSidebarToggle('tools')}
            side="right"
          >
            <TranscriptToolsSidebar
              onCollapse={() => handleSidebarCollapse('tools')}
              transcript={currentTranscript || undefined}
              audioFile={state.selectedAudioFile || undefined}
              audioFileId={state.selectedAudioFile?.id}
              editHistoryHook={editHistoryHook}
              onSpeakerUpdated={handleSpeakerUpdated}
              onTranscriptUpdated={handleTranscriptUpdated}
            />
          </CollapsibleSidebar>
        </div>
      </div>

      <UserPreferencesModal
        isOpen={isPreferencesModalOpen}
        onClose={handleClosePreferences}
      />

      {/* Floating Help Bubble */}
      <HelpBubble />
    </div>
  );
};

export default Dashboard;
